import {
    AlertTriangle,
    ArrowRight,
    BookOpenText,
    CheckCircle2,
    CircleDashed,
    Clock3,
    LoaderCircle,
    PlayCircle,
    RefreshCw,
    ShieldCheck,
    Users,
    WandSparkles,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FaArrowRightLong } from 'react-icons/fa6'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './PayrollSetupScreen.module.css'

const STEP_BLUEPRINT = [
    {
        stepKey: 'COMPLIANCE_CONFIG',
        label: 'Statutory Compliance Setup',
        description: 'Configure PF, ESI, Professional Tax, and minimum wage for this salon.',
        isCritical: true,
    },
    {
        stepKey: 'CYCLE_CONFIG',
        label: 'Payroll Cycle Setup',
        description: 'Set pay frequency, pay day, and grace period for late arrivals.',
        isCritical: true,
    },
    {
        stepKey: 'HOLIDAY_MASTER',
        label: 'Holiday Calendar',
        description: 'National holidays are pre-loaded. Optionally add salon-specific days off.',
        isCritical: false,
    },
    {
        stepKey: 'SALARY_STRUCTURES',
        label: 'Salary Structures',
        description: 'Create salary templates and assign one to each staff member.',
        isCritical: true,
    },
    {
        stepKey: 'INCENTIVE_STRUCTURES',
        label: 'Commission / Incentive Plans',
        description: 'Assign service commission and bonus plans. Optional for fixed-salary staff.',
        isCritical: false,
    },
    {
        stepKey: 'LEAVE_POLICY',
        label: 'Leave Policy',
        description: 'Define CL, SL, PL entitlements, carry-forward rules, and encashment settings.',
        isCritical: true,
    },
    {
        stepKey: 'LEAVE_BALANCES',
        label: 'Leave Balances Initialized',
        description: 'Initialize yearly CL, SL, and PL leave banks for each active staff member.',
        isCritical: true,
    },
]

const STEP_ROUTE_MAP = {
    COMPLIANCE_CONFIG: '/payroll/setup/compliance-config',
    CYCLE_CONFIG: '/payroll/setup/cycle-config',
    HOLIDAY_MASTER: '/payroll/setup/holiday-master',
    SALARY_STRUCTURES: '/payroll/setup/salary-structure',
    INCENTIVE_STRUCTURES: '/payroll/setup/incentive-structure',
    LEAVE_POLICY: '/payroll/setup/leave-policy',
    LEAVE_BALANCES: '/payroll/setup/leave-balance-init',
}

const STEP_SCREEN_NAME_MAP = {
    COMPLIANCE_CONFIG: 'ComplianceConfigScreen',
    CYCLE_CONFIG: 'CycleConfigScreen',
    HOLIDAY_MASTER: 'HolidayMasterScreen',
    SALARY_STRUCTURES: 'SalaryStructureScreen',
    INCENTIVE_STRUCTURES: 'IncentiveStructureScreen',
    LEAVE_POLICY: 'LeavePolicyScreen',
    LEAVE_BALANCES: 'LeaveBalanceInitScreen',
}

const STEP_READINESS_FILTER_MAP = {
    SALARY_STRUCTURES: 'BLOCKED',
    LEAVE_BALANCES: 'BLOCKED',
    INCENTIVE_STRUCTURES: 'WARNING',
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const buildDefaultPayload = () => ({
    overallReady: false,
    setupProgressPercent: 0,
    criticalBlockers: [],
    setupSteps: [],
    summary: {
        totalActiveStaff: 0,
        staffWithoutSalaryAssignment: 0,
        staffWithoutIncentiveAssignment: 0,
        staffWithoutLeaveBalance: 0,
        staffWithoutShift: 0,
    },
})

const mergeSetupSteps = (apiSteps) => {
    const incoming = new Map((Array.isArray(apiSteps) ? apiSteps : []).map((step) => [step?.stepKey, step]))

    return STEP_BLUEPRINT.map((step) => {
        const api = incoming.get(step.stepKey) || {}
        return {
            ...step,
            ...api,
            label: api?.label || step.label,
            description: api?.description || step.description,
            isCritical: typeof api?.isCritical === 'boolean' ? api.isCritical : step.isCritical,
            status: (api?.status || 'PENDING').toUpperCase(),
        }
    })
}

const getProgressTone = (progress) => {
    if (progress >= 100) return 'green'
    if (progress >= 40) return 'amber'
    return 'red'
}

const getStepStatusMeta = (status) => {
    if (status === 'COMPLETE') {
        return {
            label: 'Complete',
            toneClass: styles.statusComplete,
            icon: CheckCircle2,
            actionLabel: null,
        }
    }

    if (status === 'PARTIAL') {
        return {
            label: 'In Progress',
            toneClass: styles.statusPartial,
            icon: Clock3,
            actionLabel: 'Continue',
        }
    }

    return {
        label: 'Pending',
        toneClass: styles.statusPending,
        icon: CircleDashed,
        actionLabel: 'Set Up',
    }
}

const resolveStaffProgress = (step) => {
    if (step?.stepKey === 'SALARY_STRUCTURES' || step?.stepKey === 'INCENTIVE_STRUCTURES') {
        const covered = Number(step?.staffCovered || 0)
        const total = Number(step?.totalActiveStaff || 0)
        return { current: covered, total }
    }

    if (step?.stepKey === 'LEAVE_BALANCES') {
        const initialized = Number(step?.staffInitialized || 0)
        const total = Number(step?.totalActiveStaff || 0)
        return { current: initialized, total }
    }

    return null
}

const PayrollSetupScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const storeIdentifier = activeStore?.storeIdentifier || ''

    const [isLoading, setIsLoading] = useState(true)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [apiMessage, setApiMessage] = useState('')
    const [data, setData] = useState(buildDefaultPayload())
    const [animatedProgress, setAnimatedProgress] = useState(0)
    const animationFrameRef = useRef(null)

    const fetchPayrollSetup = useCallback(async ({ silent = false } = {}) => {
        if (!storeIdentifier) {
            setErrorMessage('Please select a store first to configure payroll.')
            setData(buildDefaultPayload())
            setIsLoading(false)
            return
        }

        if (!silent) {
            setIsLoading(true)
        } else {
            setIsRefreshing(true)
        }
        setErrorMessage('')

        try {
            const response = await fetch(BASE_URL_API + 'getPayrollSetupStatus', {
                method: 'POST',
                cache: 'no-store',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                }),
            })

            if (!response.ok) {
                throw new Error('Server Error')
            }

            const result = await response.json()

            if (result?.status === 'success') {
                const payload = result?.response || buildDefaultPayload()
                setApiMessage(result?.message || '')
                setData({
                    ...buildDefaultPayload(),
                    ...payload,
                    criticalBlockers: Array.isArray(payload?.criticalBlockers) ? payload.criticalBlockers : [],
                    setupSteps: mergeSetupSteps(payload?.setupSteps),
                    summary: {
                        ...buildDefaultPayload().summary,
                        ...(payload?.summary || {}),
                    },
                })
                return
            }

            if (result?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            setErrorMessage(result?.message || 'Unable to fetch payroll setup status.')
        } catch {
            setErrorMessage('Unable to connect to server. Please try again.')
        } finally {
            setIsLoading(false)
            setIsRefreshing(false)
        }
    }, [authCtx, storeIdentifier])

    useEffect(() => {
        fetchPayrollSetup({ silent: false })
    }, [fetchPayrollSetup])

    useEffect(() => {
        if (!storeIdentifier) return
        fetchPayrollSetup({ silent: true })
    }, [location.key, fetchPayrollSetup, storeIdentifier])

    useEffect(() => {
        const onFocus = () => {
            if (!storeIdentifier) return
            fetchPayrollSetup({ silent: true })
        }

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') onFocus()
        }

        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibilityChange)

        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
    }, [fetchPayrollSetup, storeIdentifier])

    useEffect(() => {
        const target = Math.max(0, Math.min(100, Number(data?.setupProgressPercent || 0)))

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
        }

        let startTime
        const duration = 700

        const animate = (time) => {
            if (!startTime) startTime = time
            const elapsed = time - startTime
            const progress = Math.min(elapsed / duration, 1)
            setAnimatedProgress(Math.round(target * progress))
            if (progress < 1) {
                animationFrameRef.current = requestAnimationFrame(animate)
            }
        }

        animationFrameRef.current = requestAnimationFrame(animate)

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current)
            }
        }
    }, [data?.setupProgressPercent])

    const progressPercent = Math.max(0, Math.min(100, Number(data?.setupProgressPercent || 0)))
    const progressTone = getProgressTone(progressPercent)
    const criticalBlockers = Array.isArray(data?.criticalBlockers) ? data.criticalBlockers : []
    const summary = data?.summary || {}

    const totalActiveStaff = Number(summary?.totalActiveStaff || 0)
    const hasNoStaff = totalActiveStaff === 0

    const canRunPayroll = Boolean(data?.overallReady) && !hasNoStaff

    const criticalPendingCount = useMemo(() => {
        if (criticalBlockers.length > 0) return criticalBlockers.length
        return (data?.setupSteps || []).filter((step) => step?.isCritical && step?.status !== 'COMPLETE').length
    }, [criticalBlockers.length, data?.setupSteps])

    const onStepAction = (step) => {
        if (!step) return

        let route = STEP_ROUTE_MAP[step.stepKey]
        if (step?.stepKey === 'CYCLE_CONFIG' && step?.status === 'COMPLETE') {
            route = '/payroll/setup/cycle-config/view'
        }

        if (!route) return

        navigate(route, {
            state: {
                source: '/payroll/setup',
                setupStepKey: step?.stepKey,
                setupScreenName: STEP_SCREEN_NAME_MAP[step?.stepKey],
            },
        })
    }

    const onRunPayroll = () => {
        if (!canRunPayroll) return
        navigate('/payroll/run-history')
    }

    const openStaffReadiness = (preferredFilter = 'ALL', from = 'setup') => {
        const normalizedFilter = {
            BLOCKED: 'SALARY_MISSING',
            WARNING: 'INCENTIVE_MISSING',
        }[preferredFilter] || preferredFilter || 'ALL'

        navigate('/payroll/staff-assignments', {
            state: {
                source: '/payroll/setup',
                from,
                preferredFilter: normalizedFilter,
            },
        })
    }

    const ringStyle = {
        '--ring-fill': `${animatedProgress}%`,
    }

    return (
        <div className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroTopRow}>
                    <span className={styles.heroPill}><WandSparkles size={14} /> Payroll Intelligence Hub</span>
                    {isRefreshing && (
                        <span className={styles.refreshHint}><RefreshCw size={13} className={styles.spinning} /> Syncing live status...</span>
                    )}
                </div>

                <div className={styles.heroMain}>
                    <div className={styles.heroCopy}>
                        <h1>Super-smart payroll setup with zero guesswork.</h1>
                        <p>
                            Configure every critical payroll step once. The checklist auto-refreshes whenever you return,
                            so your team always sees live readiness status.
                        </p>

                        <div className={styles.heroStats}>
                            <div className={styles.statChip}><ShieldCheck size={14} /> {criticalPendingCount} required step(s) pending</div>
                            <div className={styles.statChip}><Users size={14} /> {totalActiveStaff} active staff</div>
                            <div className={styles.statChip}><PlayCircle size={14} /> {canRunPayroll ? 'Payroll ready to run' : 'Complete setup to run payroll'}</div>
                        </div>
                    </div>

                    <div className={styles.progressCard}>
                        <div className={`${styles.progressRing} ${styles[`ring${progressTone}`]}`} style={ringStyle}>
                            <div className={styles.progressInner}>
                                <strong>{animatedProgress}%</strong>
                                <span>Ready</span>
                            </div>
                        </div>
                        <p>{progressPercent >= 100 ? 'All critical payroll settings are configured.' : 'Setup progress updates in real time.'}</p>
                    </div>
                </div>
            </section>

            {!storeIdentifier && (
                <section className={styles.alertCard}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>Select an active store first.</strong>
                        <p>Payroll setup is store-specific. Please select a store to continue.</p>
                    </div>
                </section>
            )}

            {errorMessage && (
                <section className={`${styles.alertCard} ${styles.errorCard}`}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>Could not load payroll setup.</strong>
                        <p>{errorMessage}</p>
                        <button type="button" onClick={() => fetchPayrollSetup({ silent: false })} className={styles.retryBtn}>
                            <RefreshCw size={14} /> Retry
                        </button>
                    </div>
                </section>
            )}

            {!errorMessage && !isLoading && !hasNoStaff && criticalPendingCount > 0 && (
                <section className={styles.alertCard}>
                    <AlertTriangle size={16} />
                    <div>
                        <strong>{criticalPendingCount} required step(s) remaining.</strong>
                        <p>Complete all critical setup items before payroll run can be enabled.</p>
                    </div>
                </section>
            )}

            {!errorMessage && !isLoading && Number(summary?.staffWithoutShift || 0) > 0 && (
                <section className={`${styles.alertCard} ${styles.softWarningCard}`}>
                    <Clock3 size={16} />
                    <div>
                        <strong>{summary?.staffWithoutShift} staff member(s) have no shift configured.</strong>
                        <p>Late deductions may be inaccurate until shifts are assigned.</p>
                    </div>
                </section>
            )}

            {!errorMessage && !isLoading && hasNoStaff && (
                <section className={styles.emptyStateCard}>
                    <Users size={20} />
                    <div>
                        <h3>Add staff to your roster before setting up payroll.</h3>
                        <p>All steps may appear complete technically, but payroll run remains disabled until active staff are available.</p>
                    </div>
                    <Link to="/manage/employees" className={styles.emptyStateAction}>
                        Manage Staff <FaArrowRightLong size={13} />
                    </Link>
                </section>
            )}

            <section className={styles.summaryGrid}>
                <button type="button" className={`${styles.summaryCard} ${styles.summaryCardButton}`} onClick={() => openStaffReadiness('BLOCKED', 'salary-summary')}>
                    <h4>Salary Assignments</h4>
                    <strong>{Number(summary?.staffWithoutSalaryAssignment || 0)}</strong>
                    <span>staff without salary template</span>
                </button>
                <button type="button" className={`${styles.summaryCard} ${styles.summaryCardButton}`} onClick={() => openStaffReadiness('WARNING', 'incentive-summary')}>
                    <h4>Incentive Assignments</h4>
                    <strong>{Number(summary?.staffWithoutIncentiveAssignment || 0)}</strong>
                    <span>staff without incentive plan</span>
                </button>
                <button type="button" className={`${styles.summaryCard} ${styles.summaryCardButton}`} onClick={() => openStaffReadiness('BLOCKED', 'leave-summary')}>
                    <h4>Leave Balance Initialization</h4>
                    <strong>{Number(summary?.staffWithoutLeaveBalance || 0)}</strong>
                    <span>staff pending leave setup</span>
                </button>
            </section>

            <div className={styles.readinessLinkRow}>
                <button type="button" className={styles.readinessLinkBtn} onClick={() => openStaffReadiness('ALL', 'summary-footer')}>
                    View Staff Readiness <ArrowRight size={14} />
                </button>
                <button type="button" className={styles.readinessLinkBtn} onClick={() => navigate('/payroll/bulk-assign-salary')}>
                    Bulk Assign Salary <ArrowRight size={14} />
                </button>
                <button
                    type="button"
                    className={styles.readinessLinkBtn}
                    onClick={() => navigate('/payroll/leave-balance-overview')}
                >
                    Leave Balance Overview <ArrowRight size={14} />
                </button>
                <button
                    type="button"
                    className={styles.readinessLinkBtn}
                    onClick={() => navigate('/payroll/advance-book')}
                >
                    Advance Book <ArrowRight size={14} />
                </button>
                <button
                    type="button"
                    className={styles.readinessLinkBtn}
                    onClick={() => navigate('/payroll/knowledge-bank')}
                >
                    <BookOpenText size={14} /> Knowledge Bank <ArrowRight size={14} />
                </button>
            </div>

            <section className={styles.checklistWrap}>
                <div className={styles.checklistHeader}>
                    <h2>Payroll Setup Checklist</h2>
                    {apiMessage && <span>{apiMessage}</span>}
                </div>

                {isLoading ? (
                    <div className={styles.loaderBox}>
                        <LoaderCircle size={20} className={styles.spinning} />
                        <p>Loading payroll setup status...</p>
                    </div>
                ) : (
                    <div className={styles.stepsGrid}>
                        {(data?.setupSteps || []).map((step) => {
                            const status = getStepStatusMeta(step?.status)
                            const StatusIcon = status.icon
                            const staffProgress = resolveStaffProgress(step)
                            const isActionEnabled =
                                Boolean(STEP_ROUTE_MAP[step?.stepKey]) &&
                                (step?.status !== 'COMPLETE' || step?.stepKey === 'CYCLE_CONFIG')
                            const staffPct = staffProgress && staffProgress.total > 0
                                ? Math.min(100, Math.round((staffProgress.current / staffProgress.total) * 100))
                                : 0

                            return (
                                <article key={step.stepKey} className={styles.stepCard}>
                                    <div className={styles.stepTopRow}>
                                        <span className={`${styles.statusPill} ${status.toneClass}`}>
                                            <StatusIcon size={14} /> {status.label}
                                        </span>
                                        {step?.isCritical && <span className={styles.criticalTag}>Critical</span>}
                                    </div>

                                    <h3>{step?.label}</h3>
                                    <p>{step?.description}</p>

                                    {staffProgress && (
                                        <button
                                            type="button"
                                            className={`${styles.inlineProgressWrap} ${styles.inlineProgressButton}`}
                                            onClick={() => openStaffReadiness(STEP_READINESS_FILTER_MAP[step?.stepKey] || 'ALL', `step-${step?.stepKey}`)}
                                        >
                                            <div className={styles.inlineProgressLabel}>
                                                <span>{staffProgress.current} of {staffProgress.total} staff configured</span>
                                                <span>{staffPct}%</span>
                                            </div>
                                            <div className={styles.inlineProgressTrack}>
                                                <span style={{ width: `${staffPct}%` }}></span>
                                            </div>
                                        </button>
                                    )}

                                    <div className={styles.stepFooterRow}>
                                        {step?.completedOn
                                            ? <span className={styles.metaText}>Completed on {step?.completedOn}</span>
                                            : <span className={styles.metaText}>{step?.isCritical ? 'Required before payroll run' : 'Recommended for higher accuracy'}</span>}

                                        {status.actionLabel && (
                                            <button
                                                type="button"
                                                className={styles.stepActionBtn}
                                                onClick={() => onStepAction(step)}
                                                disabled={!isActionEnabled}
                                            >
                                                {status.actionLabel} <ArrowRight size={14} />
                                            </button>
                                        )}
                                    </div>
                                </article>
                            )
                        })}
                    </div>
                )}
            </section>

            <section className={styles.runCard}>
                <div>
                    <h3>Ready to run payroll?</h3>
                    <p>
                        Run payroll only when all critical setup steps are complete and staff roster is active.
                    </p>
                </div>
                <button
                    type="button"
                    className={`${styles.runBtn} ${!canRunPayroll ? styles.runBtnDisabled : ''}`}
                    onClick={onRunPayroll}
                    disabled={!canRunPayroll}
                    style={!canRunPayroll ? { opacity: 0.4 } : undefined}
                >
                    Run Payroll <FaArrowRightLong size={14} />
                </button>
            </section>
        </div>
    )
}

export default PayrollSetupScreen